import React, { useState, useEffect, useRef } from "react";
import {
  green_left,
  green_right,
  home,
} from "../../assets/images/images";
import "./appointment.css";

function Appointment(props) {
  const [formData, setFormData] = useState({
    serviceType: "",
    doctor: "",
    patientName: "",
    age: "",
    date: "",
    time: "",
  });

  const [openDropdown, setOpenDropdown] = useState(null);
  const ageDropdownRef = useRef(null);
  const dateDropdownRef = useRef(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const doctorsByService = {
    Ayurvedic: [
      {
        name: "Dr. Abhijeet Shirkande",
        specialization: "M.D. (Ayurved), Ph.D",
        phone: "+91 99752 53664",
      },
      {
        name: "Dr. Ankita Shirkande",
        specialization: "M.D. (Ayurved), Ph.D",
        phone: "+91 99752 53664",
      },
    ],
    Dental: [
      {
        name: "Dr. Amol Shirkande",
        specialization: "M.D.S. Orthodontic",
        phone: "+91 99752 53664",
      },
      {
        name: "Dr. Sapana Shirkande",
        specialization: "M.D.S. Pediatric",
        phone: "+91 99752 53664",
      },
    ],
  };

  const ageOptions = Array.from({ length: 70 }, (_, i) => i + 1);

  const timeSlots = [
    "9:00 AM - 10:00 AM",
    "10:00 AM - 11:00 AM",
    "11:00 AM - 12:00 PM",
    "12:00 PM - 1:00 PM",
    "1:00 PM - 2:00 PM",
    "2:00 PM - 3:00 PM",
    "3:00 PM - 4:00 PM",
    "4:00 PM - 5:00 PM",
    "5:00 PM - 6:00 PM",
    "6:00 PM - 7:00 PM",
  ];

  // Handle clicking outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only close if clicking outside both dropdowns
      const isOutsideAge = !ageDropdownRef.current?.contains(event.target);
      const isOutsideDate = !dateDropdownRef.current?.contains(event.target);

      if (openDropdown === "age" && isOutsideAge) {
        setOpenDropdown(null);
      } else if (openDropdown === "date" && isOutsideDate) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown]);

  // Calendar helper functions
  const changeMonth = (direction, event) => {
    // Prevent event bubbling to avoid closing the calendar
    event.stopPropagation();

    let newMonth = currentMonth + direction;
    let newYear = currentYear;

    if (newMonth < 0) {
      newMonth = 11;
      newYear = currentYear - 1;
    } else if (newMonth > 11) {
      newMonth = 0;
      newYear = currentYear + 1;
    }

    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
  };

  const getCalendarDays = () => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);

      const isCurrentMonth = date.getMonth() === currentMonth;
      const isToday = date.getTime() === today.getTime();
      const isPast = date < today;

      days.push({
        day: date.getDate(),
        dateString: date.toISOString().split("T")[0],
        isCurrentMonth,
        isToday,
        isPast,
      });
    }

    return days;
  };

  const selectDate = (dateString, event) => {
    // Prevent event bubbling to avoid closing the calendar prematurely
    event.stopPropagation();

    setFormData((prevState) => ({
      ...prevState,
      date: dateString,
    }));
    setOpenDropdown(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Handle age input validation
    if (name === "age") {
      const ageValue = parseInt(value);
      if (ageValue < 1 || ageValue > 120) {
        return; // Don't update if age is out of range
      }
    }

    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    if (name === "serviceType") {
      setFormData((prevState) => ({
        ...prevState,
        doctor: "", // Reset the doctor when service type changes
      }));
    }
  };

  const handleDropdownSelect = (field, value) => {
    setFormData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
    setOpenDropdown(null);
  };

  const toggleDropdown = (field) => {
    setOpenDropdown(openDropdown === field ? null : field);
  };

  const handleBookAppointment = () => {
    const { serviceType, doctor, patientName, age, date, time } = formData;

    // Basic validation
    if (!serviceType || !patientName || !age || !date || !time) {
      alert(
        "Please fill in all required fields before booking an appointment."
      );
      return;
    }

    // Find the selected doctor from the service type
    const selectedDoctor = doctorsByService[serviceType]?.find(
      (d) => d.name === doctor
    );

    // Use the selected doctor's phone number, or default to Dr. Abhijeet if no doctor selected
    const phoneNumber = selectedDoctor?.phone || "+91 99752 53664";

    // Ensure phone number is defined before proceeding
    if (!phoneNumber) {
      alert("Unable to get doctor's contact information. Please try again.");
      return;
    }

    const message = `=== APPOINTMENT BOOKING REQUEST ===

Service: ${serviceType}
Doctor: ${doctor || "Any Available Doctor"}
Patient: ${patientName}
Date: ${new Date(date).toLocaleDateString("en-IN", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    })}
Time: ${time}
Age: ${age} years

Please confirm my appointment slot. Thank you!`;

    // Clean phone number for WhatsApp (remove spaces and +)
    const cleanPhoneNumber = phoneNumber.replace(/\s+/g, "").replace("+", "");

    const whatsappUrl = `https://wa.me/${cleanPhoneNumber}?text=${encodeURIComponent(
      message
    )}`;

    window.open(whatsappUrl, "_blank");
  };

  const renderCustomDropdown = (field, placeholder, options, selectedValue) => {
    const isOpen = openDropdown === field;

    return (
      <div className="custom-dropdown">
        <div
          className={`dropdown-header ${isOpen ? "active" : ""}`}
          onClick={() => toggleDropdown(field)}
        >
          <span className={selectedValue ? "selected" : "placeholder"}>
            {selectedValue || placeholder}
          </span>
          <div className={`dropdown-arrow ${isOpen ? "up" : "down"}`}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="6,9 12,15 18,9"></polyline>
            </svg>
          </div>
        </div>
        {isOpen && (
          <div className="dropdown-options">
            {options.map((option, index) => (
              <div
                key={index}
                className={`dropdown-option ${selectedValue === option ? "selected" : ""
                  }`}
                onClick={() => handleDropdownSelect(field, option)}
              >
                {option}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderDoctorDropdown = () => {
    const { serviceType } = formData;
    const doctors = doctorsByService[serviceType] || [];

    return (
      <div className="custom-dropdown">
        <div
          className={`dropdown-header ${openDropdown === "doctor" ? "active" : ""
            }`}
          onClick={() => toggleDropdown("doctor")}
        >
          <span className={formData.doctor ? "selected" : "placeholder"}>
            {formData.doctor || "Select Doctor"}
          </span>
          <div
            className={`dropdown-arrow ${openDropdown === "doctor" ? "up" : "down"
              }`}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="6,9 12,15 18,9"></polyline>
            </svg>
          </div>
        </div>
        {openDropdown === "doctor" && (
          <div className="dropdown-options">
            {doctors.length > 0 ? (
              doctors.map((doctor, index) => (
                <div
                  key={index}
                  className={`dropdown-option ${formData.doctor === doctor.name ? "selected" : ""
                    }`}
                  onClick={() => handleDropdownSelect("doctor", doctor.name)}
                >
                  <div className="doctor-name">{doctor.name}</div>
                  <div className="doctor-specialization">
                    {doctor.specialization}
                  </div>
                  <div className="doctor-phone">{doctor.phone}</div>
                </div>
              ))
            ) : (
              <div className="dropdown-option disabled">
                <div className="doctor-name">Please select a service first</div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="main-search-section">
        <img src={green_right} alt="shadow" className="left-shadow" />
        <img src={green_left} alt="shadow" className="right-shadow" />
        <div className="search-container">
          <div className="row">
            <div className="col-lg-2 border-line">
              {renderCustomDropdown(
                "serviceType",
                "Select Service",
                ["Dental", "Ayurvedic"],
                formData.serviceType
              )}
            </div>
            <div className="col-lg-2 border-line">
              {renderDoctorDropdown()}
              {/* {formData.doctor ? (
                <div className="doctor-phone-info">
                  <small>📞 {doctorsByService[formData.serviceType]?.find(d => d.name === formData.doctor)?.phone}</small>
                </div>
              ) : formData.serviceType && (
                <div className="doctor-phone-info default">
                  <small>📞 Default: Dr. Abhijeet Shirkande (+91 99752 53664)</small>
                </div>
              )} */}
            </div>
            <div className="col-lg-3 border-line">
              <input
                type="search"
                placeholder="Patient Name"
                className="custom-input"
                name="patientName"
                value={formData.patientName}
                onChange={handleChange}
              />
            </div>
            <div className="col-lg-1 border-line">
              <div className="age-input-container" ref={ageDropdownRef}>
                <input
                  type="number"
                  placeholder="Age"
                  className="custom-input age-input"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  min="1"
                  max="120"
                />
                <div
                  className="age-dropdown-toggle"
                  onClick={() => toggleDropdown("age")}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="6,9 12,15 18,9"></polyline>
                  </svg>
                </div>
                {openDropdown === "age" && (
                  <div className="age-dropdown-options">
                    {ageOptions.map((age, index) => (
                      <div
                        key={index}
                        className={`age-dropdown-option ${formData.age == age ? "selected" : ""
                          }`}
                        onClick={() => handleDropdownSelect("age", age)}
                      >
                        {age}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="col-lg-2 border-line">
              <div className="date-input-container" ref={dateDropdownRef}>
                <input
                  type="text"
                  placeholder="Select Date"
                  className="custom-input date-input"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  readOnly
                  onClick={() => toggleDropdown("date")}
                />
                <div
                  className="date-dropdown-toggle"
                  onClick={() => toggleDropdown("date")}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
                  </svg>
                </div>
                {openDropdown === "date" && (
                  <div
                    className="date-dropdown-options"
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <div className="calendar-header">
                      <button
                        className="calendar-nav-btn"
                        onClick={(e) => changeMonth(-1, e)}
                        disabled={
                          currentMonth === 0 &&
                          currentYear === new Date().getFullYear()
                        }
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="15,18 9,12 9,6"></polyline>
                        </svg>
                      </button>
                      <span className="calendar-month-year">
                        {new Date(currentYear, currentMonth).toLocaleDateString(
                          "en-US",
                          { month: "long", year: "numeric" }
                        )}
                      </span>
                      <button
                        className="calendar-nav-btn"
                        onClick={(e) => changeMonth(1, e)}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="9,18 15,12 9,6"></polyline>
                        </svg>
                      </button>
                    </div>
                    <div className="calendar-weekdays">
                      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                        (day) => (
                          <div key={day} className="weekday">
                            {day}
                          </div>
                        )
                      )}
                    </div>
                    <div className="calendar-days">
                      {getCalendarDays().map((day, index) => (
                        <div
                          key={index}
                          className={`calendar-day ${day.isCurrentMonth ? "" : "other-month"
                            } ${day.isToday ? "today" : ""} ${formData.date === day.dateString ? "selected" : ""
                            } ${day.isPast ? "past" : ""}`}
                          onClick={(e) =>
                            day.isCurrentMonth && !day.isPast
                              ? selectDate(day.dateString, e)
                              : null
                          }
                        >
                          {day.day}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="col-lg-2">
              {renderCustomDropdown(
                "time",
                "Select Time Slot",
                timeSlots,
                formData.time
              )}
            </div>
          </div>
        </div>
      </div>
      <section className="container-fluid margin-auto">
        <div className="row">
          <h3 className="attractive-headline">
            "From Oral Care to Overall Care"
          </h3>
          <div className="col-lg-12">
            <div className="row appointement-booking">
              <div className="book-container">
                <button
                  className="Book-Appoinment-btn"
                  onClick={handleBookAppointment}
                >
                  Book Appointment
                </button>
              </div>
              <div className="home-image-container">
                <img src={home} alt="home" className="img-wdt" />
              </div>
              <div className="counter-section">
                <div className="counts">
                  <h6>08</h6>
                  <p>Satisfaction Rate</p>
                </div>
                <div className="counts">
                  <h6>14+</h6>
                  <p>Years experience</p>
                </div>
                <div className="counts">
                  <h6>2500+</h6>
                  <p>Patients</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Appointment;
